import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ResponsiveGridLayout, useContainerWidth, noCompactor, type Layout, type ResponsiveLayouts } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { X, Save, Undo2, RotateCcw, Plus } from "lucide-react";
import { getWidgetDef, buildDefaultLayout, WIDGET_REGISTRY, type WidgetDefinition } from "./widgetRegistry";
import { insertDashboardWidget, resolveDashboardCollisions, placeDashboardWidget } from "./layoutInsertion";
import { WidgetRenderer } from "./WidgetRenderer";
import { WidgetDesignPreview } from "./WidgetDesignPreview";

interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

type BreakpointKey = "lg" | "md" | "sm" | "xs" | "xxs";

const BREAKPOINTS: Record<BreakpointKey, number> = {
  lg: 1100,
  md: 800,
  sm: 600,
  xs: 400,
  xxs: 0,
};

const COLS: Record<BreakpointKey, number> = {
  lg: 12,
  md: 6,
  sm: 2,
  xs: 1,
  xxs: 1,
};

function generateResponsiveLayouts(lgLayout: LayoutItem[]): ResponsiveLayouts<BreakpointKey> {
  const lg = lgLayout;

  const scaleLayout = (cols: number): LayoutItem[] => {
    const scaled: LayoutItem[] = [];
    let currentY = 0;

    const sorted = [...lg].sort((a, b) => a.y - b.y || a.x - b.x);

    let rowItems: LayoutItem[] = [];
    let lastY = -1;

    const flushRow = () => {
      if (rowItems.length === 0) return;
      let rowX = 0;
      let lineMaxH = 0;

      for (const item of rowItems) {
        const def = getWidgetDef(item.i);
        const minW = def?.minW ?? 1;
        const isKpi = item.i.startsWith("kpi-");

        let w: number;
        if (isKpi && cols === 6) {
          w = 3;
        } else {
          w = Math.round((item.w / 12) * cols);
          w = Math.max(w, Math.min(minW, cols));
          w = Math.min(w, cols);
        }

        if (rowX + w > cols) {
          currentY += lineMaxH || 3;
          rowX = 0;
          lineMaxH = 0;
        }

        scaled.push({
          i: item.i,
          x: rowX,
          y: currentY,
          w,
          h: item.h,
          minW: Math.min(minW, cols),
          minH: item.minH,
        });
        lineMaxH = Math.max(lineMaxH, item.h);
        rowX += w;
      }
      currentY += lineMaxH || 3;
      rowItems = [];
    };

    for (const item of sorted) {
      if (item.y !== lastY) {
        flushRow();
        lastY = item.y;
      }
      rowItems.push(item);
    }
    flushRow();

    return scaled;
  };

  const stackLayout = (cols: number): LayoutItem[] => {
    const sorted = [...lg].sort((a, b) => a.y - b.y || a.x - b.x);
    let y = 0;
    return sorted.map(item => {
      const result: LayoutItem = {
        i: item.i,
        x: 0,
        y,
        w: cols,
        h: item.h,
        minW: 1,
        minH: item.minH,
      };
      y += item.h;
      return result;
    });
  };

  return {
    lg,
    md: scaleLayout(6),
    sm: scaleLayout(2),
    xs: stackLayout(1),
    xxs: stackLayout(1),
  };
}

const LAYOUT_VERSION = 4;

function getLocalStorageKey(brandId: number) {
  return `dashboard-layout-v${LAYOUT_VERSION}-${brandId}`;
}

function loadLocalLayout(brandId: number): LayoutItem[] | null {
  try {
    const stored = localStorage.getItem(getLocalStorageKey(brandId)) || localStorage.getItem(`dashboard-layout-v3-${brandId}`);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

function clearLocalLayout(brandId: number) {
  try {
    localStorage.removeItem(getLocalStorageKey(brandId));
  } catch {}
}

function getActiveWidgetIds(layout: LayoutItem[]): string[] {
  return layout.map(l => l.i);
}

interface DashboardLayoutEngineProps {
  brandId: number;
  isEditMode: boolean;
  onEditModeChange?: (editMode: boolean) => void;
}

export function DashboardLayoutEngine({ brandId, isEditMode, onEditModeChange }: DashboardLayoutEngineProps) {
  const { width, containerRef } = useContainerWidth({ initialWidth: 1200 });
  const queryClient = useQueryClient();
  const layoutKey = `v${LAYOUT_VERSION}-${brandId}`;

  const { data: serverLayout, isLoading: isLoadingLayout } = useQuery({
    queryKey: ["dashboard-layout", layoutKey],
    queryFn: async () => {
      const res = await fetch(`/api/dashboard-layouts/${encodeURIComponent(layoutKey)}`);
      if (!res.ok) throw new Error("Failed to load layout");
      let data = await res.json();
      // Copy-on-save migration: leave v3 untouched so a rollback can still read it.
      if (!data?.layoutJson) {
        const legacy = await fetch(`/api/dashboard-layouts/v3-${brandId}`);
        if (!legacy.ok) throw new Error("Failed to load saved dashboard layout");
        data = await legacy.json();
      }
      if (data && data.layoutJson) {
        return JSON.parse(data.layoutJson) as LayoutItem[] | Record<BreakpointKey, LayoutItem[]>;
      }
      return null;
    },
    staleTime: Infinity,
  });

  const saveMutation = useMutation({
    mutationFn: async (layout: LayoutItem[] | Partial<Record<BreakpointKey, LayoutItem[]>>) => {
      const res = await fetch(`/api/dashboard-layouts/${encodeURIComponent(layoutKey)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ layoutJson: JSON.stringify(layout) }),
      });
      if (!res.ok) throw new Error("Failed to save layout");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-layout", layoutKey] });
    },
  });

  const [lgLayout, setLgLayout] = useState<LayoutItem[]>(buildDefaultLayout());
  const [responsiveEdits, setResponsiveEdits] = useState<Partial<Record<BreakpointKey,LayoutItem[]>>>({});
  const responsiveSnapshotRef = useRef<Partial<Record<BreakpointKey,LayoutItem[]>>>({});
  const [initialized, setInitialized] = useState(false);
  const snapshotRef = useRef<LayoutItem[]>(lgLayout);
  const [isDirty, setIsDirty] = useState(false);
  const [trayOpen, setTrayOpen] = useState(false);
  const dragSnapshotRef = useRef<LayoutItem[] | null>(null);
  const [dropNotice, setDropNotice] = useState('');
  // During a drag, leave every other card fixed. Resize and programmatic size
  // changes still resolve collisions, without compacting intentional spaces.
  const workspaceCompactor = useMemo(() => ({...noCompactor, type:'vertical' as const, allowOverlap:true,
    compact:(layout:Layout)=>dragSnapshotRef.current ? layout.map(item=>({...item})) : resolveDashboardCollisions(layout),
  }), []);
  // RGL does not fire onBreakpointChange for its initial mount. Derive this
  // from the same measured width so the first small-screen edit stays small-screen.
  const currentBreakpoint: BreakpointKey = width > 1100 ? "lg" : width > 800 ? "md" : width > 600 ? "sm" : width > 400 ? "xs" : "xxs";

  useEffect(() => {
    if (isLoadingLayout) return;

    if (serverLayout) {
      const base = Array.isArray(serverLayout) ? serverLayout : serverLayout.lg;
      const edits = Array.isArray(serverLayout) ? {} : Object.fromEntries(Object.entries(serverLayout).filter(([key]) => key !== 'lg'));
      setLgLayout(base);
      snapshotRef.current = base;
      setResponsiveEdits(edits);
      responsiveSnapshotRef.current = edits;
      clearLocalLayout(brandId);
    } else {
      setResponsiveEdits({});
      responsiveSnapshotRef.current = {};
      const local = loadLocalLayout(brandId);
      if (local) {
        setLgLayout(local);
        snapshotRef.current = local;
        saveMutation.mutate(local);
        clearLocalLayout(brandId);
      } else {
        const def = buildDefaultLayout();
        setLgLayout(def);
        snapshotRef.current = def;
      }
    }
    setIsDirty(false);
    setInitialized(true);
  }, [isLoadingLayout, serverLayout, brandId]);

  useEffect(() => {
    // Editing starts unobstructed; Add Widgets opens the picker on demand.
    setTrayOpen(false);
    setDropNotice('');
    if (isEditMode) {
      snapshotRef.current = lgLayout;
      responsiveSnapshotRef.current = responsiveEdits;
      setIsDirty(false);
    }
  }, [isEditMode]);

  const activeWidgetIds = useMemo(() => getActiveWidgetIds(lgLayout), [lgLayout]);

  const responsiveLayouts = useMemo(() => ({...generateResponsiveLayouts(lgLayout), ...responsiveEdits}), [lgLayout, responsiveEdits]);

  const currentBreakpointRef = useRef(currentBreakpoint);
  currentBreakpointRef.current = currentBreakpoint;

  const handleInteractionStop = useCallback((currentLayout: Layout) => {
    const updated: LayoutItem[] = currentLayout.map(l => ({
      i: l.i,
      x: l.x,
      y: l.y,
      w: l.w,
      h: l.h,
      minW: l.minW,
      minH: l.minH,
    }));
    if(currentBreakpointRef.current === "lg") setLgLayout(updated);
    else setResponsiveEdits(previous => ({...previous,[currentBreakpointRef.current]:updated}));
    setIsDirty(true);
  }, []);

  const handleDragStart = useCallback((layout:Layout) => {
    dragSnapshotRef.current=layout.map(item=>({...item}));
    setDropNotice('');
  }, []);

  const handleDragStop = useCallback((layout:Layout, _old:unknown, moved:Layout[number]|null) => {
    const before=dragSnapshotRef.current;
    dragSnapshotRef.current=null;
    if(!before||!moved)return;
    const result=placeDashboardWidget(before,moved,COLS[currentBreakpointRef.current]);
    // RGL commits its callback layout after onDragStop. Reconcile that same
    // layout so a rejected/no-op drop also restores the visible internal grid.
    for(const item of layout){const placed=result.layout.find(next=>next.i===item.i);if(placed)Object.assign(item,placed);}
    handleInteractionStop(result.layout);
    setDropNotice(result.rejected?'That tile does not fit in this space. Its original position has been restored.':'');
  }, [handleInteractionStop]);

  const removeWidget = useCallback((widgetId: string) => {
    setLgLayout(prev => prev.filter(l => l.i !== widgetId));
    setResponsiveEdits(prev => Object.fromEntries(Object.entries(prev).map(([key,layout]) => [key,layout!.filter(item=>item.i!==widgetId)])));
    setIsDirty(true);
  }, []);

  const addWidget = useCallback((widgetId: string) => {
    const def = getWidgetDef(widgetId);
    if (!def) return;
    // Anchor to rendered cards: this page can have an independent scroll container.
    const visible = Array.from(containerRef.current?.querySelectorAll<HTMLElement>('[data-layout-widget]') ?? [])
      .filter(card => {const rect=card.getBoundingClientRect();return rect.bottom>80&&rect.top<window.innerHeight-100;})
      .sort((a,b)=>a.getBoundingClientRect().top-b.getBoundingClientRect().top);
    const anchorId=visible[0]?.dataset.layoutWidget;
    const item={i:widgetId,x:0,y:0,w:def.defaultW,h:def.defaultH,minW:def.minW,minH:def.minH};
    setLgLayout(prev => insertDashboardWidget(prev,item,anchorId));
    // Materialize the current responsive view before editing so smaller screens
    // preserve their own arrangement and don't overwrite the desktop layout.
    setResponsiveEdits(prev => {
      const base={...prev};
      const bp=currentBreakpointRef.current;
      if(bp!=="lg"&&!base[bp])base[bp]=responsiveLayouts[bp] as LayoutItem[];
      return Object.fromEntries(Object.entries(base).map(([key,layout])=>[key,insertDashboardWidget(layout!,{...item,w:Math.min(item.w,COLS[key as BreakpointKey]),minW:Math.min(item.minW||1,COLS[key as BreakpointKey])},anchorId)]));
    });
    setIsDirty(true);
    // Keep the inserted card visible without jumping to the bottom of the page.
    requestAnimationFrame(() => {
      const card = containerRef.current?.querySelector<HTMLElement>(`[data-layout-widget="${widgetId}"]`);
      card?.scrollIntoView({ block: "nearest", behavior: "instant" });
    });
  }, [containerRef, responsiveLayouts]);

  const updateResponsiveSize = useCallback((widgetId:string, full:boolean) => {
    const def=getWidgetDef(widgetId);if(!def)return;
    setResponsiveEdits(prev=>Object.fromEntries(Object.entries(prev).map(([key,layout])=>[key,layout!.map(item=>item.i===widgetId?{...item,w:full?COLS[key as BreakpointKey]:Math.min(def.defaultW,COLS[key as BreakpointKey]),h:full?Math.max(item.h,6):def.defaultH}:item)])));
  },[]);
  const resetWidgetSize = useCallback((widgetId: string) => {
    const def = getWidgetDef(widgetId);
    if (!def) return;
    setLgLayout(prev =>
      prev.map(l =>
        l.i === widgetId ? { ...l, w: def.defaultW, h: def.defaultH } : l
      )
    );
    updateResponsiveSize(widgetId,false);
    setIsDirty(true);
  }, [updateResponsiveSize]);

  const resizeWidget = useCallback((widgetId: string) => {
    setLgLayout(prev =>
      prev.map(l =>
        l.i === widgetId ? { ...l, w: 12, h: Math.max(l.h, 6) } : l
      )
    );
    updateResponsiveSize(widgetId,true);
    setIsDirty(true);
  }, [updateResponsiveSize]);

  const saveChanges = useCallback(() => {
    saveMutation.mutate({lg:lgLayout,...responsiveEdits}, { onSuccess: () => {
      snapshotRef.current = lgLayout;
      responsiveSnapshotRef.current = responsiveEdits;
      setIsDirty(false);
      onEditModeChange?.(false);
    }});
  }, [lgLayout, responsiveEdits, onEditModeChange, saveMutation]);

  const cancelChanges = useCallback(() => {
    saveMutation.reset();
    setLgLayout(snapshotRef.current);
    setResponsiveEdits(responsiveSnapshotRef.current);
    setIsDirty(false);
    onEditModeChange?.(false);
  }, [onEditModeChange, saveMutation]);

  const resetToDefault = useCallback(() => {
    const defaultLayout = buildDefaultLayout();
    setLgLayout(defaultLayout);
    setResponsiveEdits({});
    setIsDirty(true);
  }, []);

  const dragConfig = useMemo(() => ({
    enabled: isEditMode && !saveMutation.isPending,
    bounded: false,
    handle: ".drag-handle",
    threshold: 3,
  }), [isEditMode, saveMutation.isPending]);

  const resizeConfig = useMemo(() => ({
    enabled: isEditMode && !saveMutation.isPending,
    handles: ["se" as const],
  }), [isEditMode, saveMutation.isPending]);

  const isLoading = !initialized || isLoadingLayout;

  // Keep the measured element mounted while the saved layout loads. Otherwise
  // useContainerWidth observes a null ref and the grid keeps its fallback width.
  return (
    <div className={`analytics-layout-engine relative min-w-0 max-w-full ${isEditMode ? "analytics-layout-editing" : ""}`}>
      <div ref={containerRef} className="w-full min-w-0" data-testid="analytics-grid-container" aria-busy={isLoading}>
        {isLoading && (
          <div className="flex items-center justify-center py-20" role="status" aria-label="Loading dashboard layout">
            <div className="animate-spin h-6 w-6 border-2 border-[#7b1f40] border-t-transparent rounded-full" />
          </div>
        )}
        {!isLoading && isEditMode && (
          <div className="analytics-edit-toolbar flex flex-wrap gap-3 items-center justify-between bg-primary/5 border border-primary/20 rounded-lg px-4 py-2.5 mb-4" data-testid="edit-mode-bar">
            <p className="text-sm text-primary font-medium">
              Editing layout {isDirty && "— unsaved changes"}
            </p>
            <div className="flex items-center gap-2">
              <button disabled={saveMutation.isPending} onClick={() => setTrayOpen(open => !open)} aria-expanded={trayOpen} aria-controls="analytics-widget-tray" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white border rounded-md" data-testid="toggle-widget-tray"><Plus className="h-3.5 w-3.5" /> Add Widgets</button>
              <button
                disabled={saveMutation.isPending}
                onClick={resetToDefault}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-md hover:bg-orange-50 transition-colors"
                data-testid="reset-layout-button"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset Layout
              </button>
              <button
                disabled={saveMutation.isPending}
                onClick={cancelChanges}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                data-testid="cancel-layout-button"
              >
                <Undo2 className="h-3.5 w-3.5" />
                Cancel
              </button>
              <button
                onClick={saveChanges}
                disabled={saveMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-[#621933] transition-colors disabled:opacity-50"
                data-testid="save-layout-button"
              >
                <Save className="h-3.5 w-3.5" />
                {saveMutation.isPending ? "Saving..." : "Save Layout"}
              </button>
            </div>
          </div>
        )}
        {isEditMode && dropNotice && <p role="status" className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 max-w-sm rounded-md border bg-white p-3 text-sm shadow-lg">{dropNotice}</p>}
        {isEditMode && saveMutation.isError && <p role="alert" className="text-red-700 bg-white border border-red-200 p-3 rounded-md">The layout could not be saved. Your edits are still here; try Save Layout again.</p>}
        {!isLoading && width > 0 && (
          <ResponsiveGridLayout
            className="layout"
            width={width}
            layouts={responsiveLayouts}
            breakpoints={BREAKPOINTS}
            cols={COLS}
            rowHeight={40}
            margin={[16, 16] as readonly [number, number]}
            dragConfig={dragConfig}
            resizeConfig={resizeConfig}
            compactor={workspaceCompactor}
            onDragStart={handleDragStart}
            onDragStop={handleDragStop}
            onResizeStop={handleInteractionStop}
          >
            {lgLayout.map(item => {
              const def = getWidgetDef(item.i);
              return (
                <div key={item.i} data-layout-widget={item.i}>
                  <WidgetRenderer
                    widgetId={item.i}
                    title={def?.title || item.i}
                    gridH={item.h}
                    isEditMode={isEditMode}
                    onRemove={isEditMode && !saveMutation.isPending ? () => removeWidget(item.i) : undefined}
                    onResize={isEditMode && !saveMutation.isPending ? () => resizeWidget(item.i) : undefined}
                    onResetSize={isEditMode && !saveMutation.isPending ? () => resetWidgetSize(item.i) : undefined}
                  />
                </div>
              );
            })}
          </ResponsiveGridLayout>
        )}
      </div>

      <WidgetTray
        disabled={saveMutation.isPending}
        activeWidgetIds={activeWidgetIds}
        onAddWidget={addWidget}
        isVisible={isEditMode && trayOpen}
        onClose={() => setTrayOpen(false)}
      />
    </div>
  );
}

interface WidgetTrayProps {
  disabled: boolean;
  activeWidgetIds: string[];
  onAddWidget: (id: string) => void;
  isVisible: boolean;
  onClose: () => void;
}

function WidgetTray({ disabled, activeWidgetIds, onAddWidget, isVisible, onClose }: WidgetTrayProps) {
  const isMobile = useIsMobile();
  const categories: Array<{ key: WidgetDefinition["category"]; label: string; badge: string }> = [
    { key: "kpi", label: "KPI Metrics", badge: "bg-primary/5 text-primary" },
    { key: "chart", label: "Charts", badge: "bg-primary/5 text-primary" },
    { key: "table", label: "Tables & Intelligence", badge: "bg-primary/5 text-primary" },
  ];

  const totalAvailable = WIDGET_REGISTRY.filter(w => !activeWidgetIds.includes(w.id)).length;

  return (
    <div
      id="analytics-widget-tray"
      aria-label="Add Widgets"
      aria-hidden={!isVisible}
      style={{ visibility: isVisible ? "visible" : "hidden" }}
      className={`analytics-widget-tray fixed right-0 top-0 bottom-0 w-[280px] bg-white border-l border-gray-200 shadow-lg z-50 overflow-y-auto transition-transform duration-300 ease-in-out ${isVisible ? "translate-x-0" : "translate-x-full"}`}
      data-testid="widget-tray"
    >
      <div className="p-4 border-b border-gray-100 flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Add Widgets</h3>
          <p className="text-xs text-gray-500 mt-1">
            {totalAvailable} widgets available to add
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="close-widget-tray"
          aria-label="Close widget tray"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-3 space-y-5">
        {categories.map(cat => {
          const widgets = WIDGET_REGISTRY.filter(w => w.category === cat.key);
          const available = widgets.filter(w => !activeWidgetIds.includes(w.id));
          const totalInCat = widgets.length;
          const activeInCat = totalInCat - available.length;

          return (
            <div key={cat.key}>
              <div className="flex items-center justify-between px-1 mb-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{cat.label}</p>
                <span className="text-[10px] text-gray-400">{activeInCat}/{totalInCat} active</span>
              </div>
              {available.length === 0 ? (
                <p className="text-[11px] text-gray-300 italic px-3 py-2">All added</p>
              ) : (
                <div className="space-y-1">
                  {available.map(w => (
                    <WidgetDesignPreview key={w.id} widget={w} compact={isMobile} enabled={isVisible && !disabled}>
                    <button
                      disabled={disabled}
                      onClick={() => onAddWidget(w.id)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-primary/5 transition-colors group flex items-start justify-between gap-2"
                      data-testid={`add-widget-${w.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-medium text-gray-700 group-hover:text-primary truncate">{w.title}</p>
                          <span className={`px-1.5 py-0 rounded text-[9px] font-semibold capitalize flex-shrink-0 ${cat.badge}`}>
                            {w.category}
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-0.5">{w.description}</p>
                      </div>
                      <span className="text-[10px] font-medium text-primary flex-shrink-0 mt-0.5">+ Add</span>
                    </button>
                    </WidgetDesignPreview>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
